import { module as BaseModule } from 'modujs';
import gsap from 'gsap';

const blurProperty = gsap.utils.checkPrefix('filter');
const blurRegex = /blur\((.+)?px\)/;
const getBlurMatch = target => (gsap.getProperty(target, blurProperty) || '').match(blurRegex) || [];

gsap.registerPlugin({
  name: 'blur',

  get(target) {
    return +(getBlurMatch(target)[1]) || 0;
  },

  init(target, endValue) {
    let data = this;
    let filter = gsap.getProperty(target, blurProperty);
    let endBlur = 'blur(' + endValue + 'px)';
    let match = getBlurMatch(target)[0];
    let index;

    if (filter === 'none') {
      filter = '';
    }

    if (match) {
      index = filter.indexOf(match);
      endValue = filter.substr(0, index) + endBlur + filter.substr(index + match.length);
    } else {
      endValue = filter + endBlur;
      filter += filter ? ' blur(0px)' : 'blur(0px)';
    }
    data.target = target;
    data.interp = gsap.utils.interpolate(filter, endValue);
  },

  render(progress, data) {
    data.target.style[blurProperty] = data.interp(progress);
  }
});

export default class extends BaseModule {
  constructor(m) {
    super(m);

    this.iObs = null;
    this.checkInitialVisibleElements = false;
    this.targets = [
      {
        element: this.el.querySelector('.c-hero-solution_eyebrow'),
        config: {
          duration: 0.8,
          delay: 0,
        }
      },
      {
        element: this.el.querySelector('.c-hero-solution_heading'),
        config: {
          duration: 1.2,
          delay: 0.2,
        }
      },
      {
        element: this.el.querySelector('.c-hero-solution_image'),
        config: {
          duration: 2.2,
          delay: 0.4
        }
      },
      {
        element: this.el.querySelector('.c-hero-solution_subheading'),
        config: {
          duration: 0.8,
          delay: window.matchMedia('(min-height: 50em)').matches ? 2.2 : 0
        }
      },
      {
        element: this.el.querySelector('.c-hero-solution_text'),
        config: {
          duration: 0.8,
          delay: window.matchMedia('(min-height: 60em)').matches ? 2.5 : 0
        }
      },
      {
        //element: this.el.querySelector('.c-solution-download:nth-child(2)'),
        element: this.el.querySelectorAll('.c-solution-download'),
        //config: {
        //  duration: 0.8,
        //  delay: window.matchMedia('(min-height: 60em)').matches ? 2.6 : 0
        //}
      },
      {
        element: this.el.querySelector('.c-overview_tabs'),
        config: {
          duration: 0.8,
        }
      },
      {
        element: this.el.querySelector('.c-summary_title'),
        config: {
          duration: 0.8,
          delay: 0.3,
        }
      },
      {
        element: this.el.querySelector('.c-summary_text'),
        config: {
          duration: 0.8,
          delay: 0.3,
        }
      },
      {
        element: this.el.querySelectorAll('.c-summary_card'),
      },
      {
        element: this.el.querySelector('.c-faq_text'),
      },
    ]
  }

  init() {
    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    if ( ! window.IntersectionObserver ) {
      return;
    }

    this.iObs = new IntersectionObserver(
      entries => this.handleIntersection( entries ),
      {
        threshold: [ 0.5, 1 ],
        //threshold: [ 1 ],
      }
    );

    this.targets.forEach( target => {
      if (target.config) {
        target.element.dataset.animation = encodeURI(
          JSON.stringify( target.config )
        );
      }

      if ( 'length' in target.element ) {
        target.element.forEach( (element, index) => {
          this.iObs.observe( element );
        } );
      } else {
        this.iObs.observe( target.element );
      }
    } );
  }

  handleIntersection( entries ) {
    entries.forEach( entry => {
      const options = 'undefined' !== typeof entry.target.dataset.animation
        ? JSON.parse( decodeURI( entry.target.dataset.animation ) )
        : {}
      ;

      if (
        (
          entry.target.classList.contains('c-hero-solution_eyebrow')
          || entry.target.classList.contains('c-hero-solution_heading')
          || entry.target.classList.contains('c-hero-solution_subheading')
          || entry.target.classList.contains('c-hero-solution_text')
          || entry.target.classList.contains('c-summary_title')
          || entry.target.classList.contains('c-summary_text')
        ) && 0.5 < entry.intersectionRatio
      ) {
        this.animateText( entry.target, options );
        this.iObs.unobserve( entry.target );
      }

      if (
        entry.target.classList.contains('c-hero-solution_image')
        && 0.5 < entry.intersectionRatio
      ) {
        this.animateSolutionImage( entry.target, options );
        this.iObs.unobserve( entry.target );
      }

      if (
        entry.target.classList.contains('c-solution-download')
        && 0.5 < entry.intersectionRatio
      ) {
        this.animateDownloadButton( entry.target, options );
        this.iObs.unobserve( entry.target );
      }

      if (
        entry.target.classList.contains('c-overview_tabs')
        && 1 === entry.intersectionRatio
      ) {
        this.animateOverviewTabs( entry.target, options );
        this.iObs.unobserve( entry.target );
      }

      if (
        entry.target.classList.contains('c-summary_card')
        && 0.5 <= entry.intersectionRatio
      ) {
        this.animateSummaryCard( entry.target, options );
        this.iObs.unobserve( entry.target );
      }

      if (
        entry.target.classList.contains('c-faq_text')
        && 1 === entry.intersectionRatio
      ) {
        this.animateFAQ( entry.target );
        this.iObs.unobserve( entry.target );
      }

    } );
  }

  animateText( target, options ) {
    gsap.to( target, {
      duration: options.duration ?? 1,
      delay: options.delay ?? 0,
      opacity: 1,
      y: 0,
      ease: 'power3.out'
    } );
  }

  animateSolutionImage( target, options ) {
    gsap.to( target, {
      duration: options.duration ?? 1,
      delay: options.delay ?? 0,
      opacity: 1,
      blur: 0,
      ease: 'power3.inOut'
    } );
  }

  animateDownloadButton( target, options ) {
    gsap.to( target, {
      duration: options.duration ?? 1,
      delay: options.delay ?? 0,
      opacity: 1,
      scale: 1,
      ease: 'power3.out'
    } );
  }

  animateOverviewTabs( target, options ) {
    gsap.to( target.children, {
      duration: options.duration ?? 1,
      opacity: 1,
      y: 1,
      stagger: 0.1,
      ease: 'power3.out',
      onComplete: () => {
        gsap.to( target.nextElementSibling, {
          duration: options.duration ?? 1,
          opacity: 1,
          ease: 'power3.out',
        } );
      }
    } );
  }

  animateSummaryCard( target, options ) {
    gsap.to( target, {
      duration: 0.8,
      delay: 0.2,
      opacity: 1,
      y: 0,
      ease: 'power3.out'
    } );
  }

  animateFAQ( target ) {
    gsap.to( target.previousElementSibling, {
      duration: 0.8,
      delay: 0.2,
      opacity: 1,
      y: 0,
      ease: 'power3.out'
    } );

    gsap.to( target, {
      duration: 0.8,
      delay: 0.4,
      opacity: 1,
      y: 0,
      ease: 'power3.out',
      onComplete: () => {
        gsap.to( target.nextElementSibling.querySelector('.c-faq_image'), {
          duration: 1.2,
          opacity: 1,
          x: 0,
          ease: 'power3.out',
        } );
        gsap.to( target.nextElementSibling.querySelector('.c-faq_list'), {
          duration: 1.2,
          opacity: 1,
          x: 0,
          ease: 'power3.out',
        } );
      }
    } );
  }
}
